import { useState } from "react";
import { Mail, Send, MessageSquare, Clock, Shield, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { CONTACT_EMAIL, MAILTO, buildGmailComposeUrl } from "@/lib/contact";
import { submitContact } from "@/lib/api";

const INITIAL = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      // 1. Always persist in the backend so no message is ever lost.
      await submitContact(form);

      // 2. Open a Gmail compose window (new tab) pre-filled with the message
      //    addressed to the site owner. The visitor just hits "Send" in Gmail.
      const gmailUrl = buildGmailComposeUrl(form);
      window.open(gmailUrl, "_blank", "noopener,noreferrer");

      toast.success("Your Gmail is opening with the message ready — click Send there to deliver it.");
      setForm(INITIAL);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
      <SEO
        title="Contact Us"
        description={`Get in touch with the Creator Intelligence Tube team. Email us at ${CONTACT_EMAIL} for support, partnerships, feedback and press inquiries.`}
        path="/contact"
        jsonLd={{
          "@type": "ContactPage",
          name: "Contact Creator Intelligence Tube",
          url: "/contact",
        }}
      />
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12">
        <div>
          <div className="overline">Contact</div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            Let's talk.
          </h1>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Questions about a channel analysis, partnership opportunities, feature requests or bug reports — we read every message.
          </p>

          <div className="mt-8 space-y-4">
            <a
              href={MAILTO}
              data-testid="contact-email-link"
              className="card-flat p-5 flex items-center gap-4 hover:border-primary/40 transition-colors group"
            >
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="overline">Email</div>
                <div className="font-semibold text-sm truncate group-hover:text-primary transition-colors">{CONTACT_EMAIL}</div>
              </div>
            </a>
            <div className="card-flat p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="overline">Response time</div>
                <div className="font-semibold text-sm">Within 1–2 business days</div>
              </div>
            </div>
            <div className="card-flat p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="overline">Privacy</div>
                <div className="font-semibold text-sm">We never share your data</div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="card-flat p-6 sm:p-10"
        >
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <MessageSquare className="w-4.5 h-4.5 text-primary" style={{ width: 18, height: 18 }} />
            </div>
            <h2 className="text-[22px] font-semibold tracking-tight">Send us a message</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="contact-name" className="block text-[13px] font-semibold text-foreground mb-1.5">
                Full Name
              </label>
              <Input
                id="contact-name"
                data-testid="contact-name"
                value={form.name}
                onChange={set("name")}
                placeholder="Jane Doe"
                className="h-11"
                required
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-[13px] font-semibold text-foreground mb-1.5">
                Email Address
              </label>
              <Input
                id="contact-email"
                data-testid="contact-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.com"
                className="h-11"
                required
              />
            </div>
            <div>
              <label htmlFor="contact-subject" className="block text-[13px] font-semibold text-foreground mb-1.5">
                Subject
              </label>
              <Input
                id="contact-subject"
                data-testid="contact-subject"
                value={form.subject}
                onChange={set("subject")}
                placeholder="How can we help?"
                className="h-11"
                required
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-[13px] font-semibold text-foreground mb-1.5">
                Message
              </label>
              <Textarea
                id="contact-message"
                data-testid="contact-message"
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us a bit more..."
                rows={6}
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="btn-primary h-11 px-6 mt-6 w-full sm:w-auto"
          >
            {loading ? "Preparing..." : (<>Send via Gmail <ExternalLink className="w-4 h-4 ml-2" /></>)}
          </Button>

          <p className="mt-4 text-[12px] text-muted-foreground">
            The button opens Gmail with your message ready to send to <span className="text-primary font-mono">{CONTACT_EMAIL}</span>. By submitting, you agree to our <a href="/legal/privacy" className="text-primary hover:underline">Privacy Policy</a>. We'll only use your email to reply.
          </p>
        </form>
      </div>
    </div>
  );
}
